import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { C, SERIF, entero, flagVista, nodoColor, programaVista, schemaEs, st } from "../theme.ts";

/** Punto de color + nombre del tipo de entidad (identidad nunca solo-color). */
export function ChipSchema({ schema }: { schema: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: C.ink }}>
      <span
        aria-hidden
        style={{
          width: 9,
          height: 9,
          borderRadius: "50%",
          background: nodoColor(schema),
          border: "1px solid rgba(0,0,0,0.15)",
          flex: "none",
        }}
      />
      {schemaEs(schema)}
    </span>
  );
}

const TONOS: Record<string, { color: string; bg: string }> = {
  rojo: { color: C.red, bg: C.redBg },
  ambar: { color: C.amber, bg: C.amberBg },
  verde: { color: C.green, bg: C.greenBg },
  neutro: { color: C.tielDark, bg: C.tielSoft },
};

/** Chip de programa de sanción (69-B con semáforo procesal; OFAC neutro tiel). */
export function ChipPrograma({ programa }: { programa: string }) {
  const v = programaVista(programa);
  const tono = TONOS[v.tono] ?? TONOS["neutro"] as { color: string; bg: string };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.02em",
        color: tono.color,
        background: tono.bg,
        whiteSpace: "nowrap",
      }}
      title={programa}
    >
      {v.etiqueta}
    </span>
  );
}

/**
 * Chip de flag fiscal ESTRUCTURADO (eje propio §7.7): tono por severidad del
 * catálogo, no por código. Borde punteado = lo distingue de una sanción a
 * primera vista (dos ejes, dos vestimentas).
 */
export function ChipFlag({ flag }: { flag: { lista: string; categoria: string; severidad: string } }) {
  const v = flagVista(flag);
  const tono = TONOS[v.tono] ?? TONOS["neutro"] as { color: string; bg: string };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.02em",
        color: tono.color,
        background: "#fff",
        border: `1.5px dashed ${tono.color}`,
        whiteSpace: "nowrap",
      }}
      title={`Situación fiscal SAT · severidad ${flag.severidad}`}
    >
      {v.etiqueta}
    </span>
  );
}

/** Cifra hero del panorama (número serif grande + etiqueta). */
export function StatTile({ etiqueta, valor, detalle }: { etiqueta: string; valor: number; detalle?: string }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: "16px 18px",
        flex: "1 1 150px",
        minWidth: 150,
      }}
    >
      <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: C.muted }}>
        {etiqueta}
      </div>
      <div style={{ fontFamily: SERIF, fontSize: 34, fontWeight: 500, color: C.ink, marginTop: 4, lineHeight: 1.1 }}>
        {entero(valor)}
      </div>
      {detalle ? <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{detalle}</div> : null}
    </div>
  );
}

export interface DatoBarra {
  etiqueta: ReactNode;
  titulo: string;
  n: number;
}

/**
 * Barras horizontales de magnitud — un solo matiz (tiel): la identidad ya la
 * carga la etiqueta de cada fila, así que el color no tiene trabajo categórico.
 * Marca delgada (12px), extremo redondeado, valor directo al final de la barra.
 */
export function BarrasH({ datos, color = C.tiel }: { datos: DatoBarra[]; color?: string }) {
  const max = Math.max(1, ...datos.map((d) => d.n));
  return (
    <div>
      {datos.map((d, i) => (
        <div
          key={i}
          title={`${d.titulo}: ${entero(d.n)}`}
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0" }}
        >
          <div
            style={{
              width: 180,
              flex: "none",
              fontSize: 12.5,
              color: C.ink,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: "right",
            }}
          >
            {d.etiqueta}
          </div>
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                // 84% del carril: la cifra directa al final siempre cabe.
                width: `${String(Math.max(0.5, (d.n / max) * 84))}%`,
                height: 12,
                background: color,
                borderRadius: "0 4px 4px 0",
                flex: "none",
                minWidth: 2,
              }}
            />
            <span style={{ fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>{entero(d.n)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function Cargando({ texto = "Cargando…" }: { texto?: string }) {
  return <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 14 }}>{texto}</div>;
}

export function ErrorBox({ mensaje }: { mensaje: string }) {
  return (
    <div
      style={{
        background: C.redBg,
        color: C.red,
        border: `1px solid ${C.red}33`,
        borderRadius: 10,
        padding: "10px 14px",
        fontSize: 13.5,
        margin: "12px 0",
      }}
      role="alert"
    >
      {mensaje}
    </div>
  );
}

/**
 * BotonesRevision — aprobar/rechazar con confirmación en dos pasos (el primer
 * clic arma, el segundo ejecuta) para poder revisar en volumen sin diálogos.
 * Genérico sobre el par de decisiones (p.ej. approved/rejected para
 * afirmaciones y resoluciones, confirmed/rejected para menciones del visor).
 */
export function BotonesRevision<D extends string>({
  aprobar,
  rechazar,
  etiquetaAprobar = "Aprobar",
  etiquetaRechazar = "Descartar",
  onDecidir,
}: {
  aprobar: D;
  rechazar: D;
  etiquetaAprobar?: string;
  etiquetaRechazar?: string;
  onDecidir: (d: D) => Promise<void>;
}) {
  const [armado, setArmado] = useState<D | null>(null);
  const [guardando, setGuardando] = useState(false);
  async function decidir(d: D): Promise<void> {
    if (armado !== d) {
      setArmado(d);
      return;
    }
    setGuardando(true);
    try {
      await onDecidir(d);
    } finally {
      setGuardando(false);
      setArmado(null);
    }
  }
  const chico = { ...st.btn, padding: "3px 8px", fontSize: 11.5 };
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <button
        type="button"
        style={{ ...chico, color: C.green, fontWeight: armado === aprobar ? 800 : 600 }}
        disabled={guardando}
        onClick={() => { void decidir(aprobar); }}
      >
        {armado === aprobar ? "¿Confirmar?" : etiquetaAprobar}
      </button>{" "}
      <button
        type="button"
        style={{ ...chico, color: C.red, fontWeight: armado === rechazar ? 800 : 600 }}
        disabled={guardando}
        onClick={() => { void decidir(rechazar); }}
      >
        {armado === rechazar ? "¿Confirmar?" : etiquetaRechazar}
      </button>
    </span>
  );
}

export const celdaTh: CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: C.muted,
  borderBottom: `1px solid ${C.line}`,
  whiteSpace: "nowrap",
};

export const celdaTd: CSSProperties = {
  padding: "9px 10px",
  fontSize: 13.5,
  color: C.ink,
  borderBottom: `1px solid ${C.line}`,
  verticalAlign: "top",
};
